import React, { useEffect, useState } from "react";
import receivableReportService, {
  FilterCondition,
  ReceivableBooking,
  ReceivableFilters,
  Project,
} from "../services/receivableReportService";
import "../css/FollowUpReport.css";

const ReceivableReport: React.FC = () => {
  // Filters
  const [selectedProject, setSelectedProject] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");

  // Dynamic filter state
  const [filterLogic, setFilterLogic] = useState<"AND" | "OR">("AND");
  const [filterConditions, setFilterConditions] = useState<FilterCondition[]>([]);

  // Data state
  const [projects, setProjects] = useState<Project[]>([]);
  const [bookings, setBookings] = useState<ReceivableBooking[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFilterBuilder, setShowFilterBuilder] = useState(false);

  // Available filter fields and operators
  const filterFields = [
    { value: "pendingPercent", label: "Pending %" },
    { value: "receivedPercent", label: "Received %" },
    { value: "totalPending", label: "Total Pending" },
    { value: "totalExpected", label: "Total Expected" },
    { value: "totalCollected", label: "Total Collected" },
    { value: "otherWorkExpected", label: "Other Work Expected" },
    { value: "otherWorkCollected", label: "Other Work Collected" },
    { value: "terraceWorkExpected", label: "Terrace Work Expected" },
    { value: "terraceWorkCollected", label: "Terrace Work Collected" },
    { value: "chargesExpected", label: "Charges Expected" },
    { value: "chargesCollected", label: "Charges Collected" },
    { value: "banakhatExpected", label: "Banakhat Expected" },
    { value: "banakhatCollected", label: "Banakhat Collected" },
    { value: "adaniGasExpected", label: "Adani Gas Expected" },
    { value: "adaniGasCollected", label: "Adani Gas Collected" },
    { value: "collectionCount", label: "Collection Count" },
    { value: "daysSinceBooking", label: "Days Since Booking" },
    { value: "daysSinceLastCollection", label: "Days Since Last Collection" },
    { value: "unitType", label: "Unit Type" },
    { value: "wing", label: "Wing" },
    { value: "status", label: "Status" },
  ];

  const numericOperators = [
    { value: ">", label: ">" },
    { value: ">=", label: ">=" },
    { value: "<", label: "<" },
    { value: "<=", label: "<=" },
    { value: "=", label: "=" },
    { value: "between", label: "Between" },
  ];

  const textOperators = [
    { value: "=", label: "=" },
    { value: "!=", label: "!=" },
  ];

  const operatorsByField: Record<string, { value: string; label: string }[]> = {
    pendingPercent: numericOperators,
    receivedPercent: numericOperators,
    totalPending: numericOperators,
    totalExpected: numericOperators,
    totalCollected: numericOperators,
    otherWorkExpected: numericOperators,
    otherWorkCollected: numericOperators,
    terraceWorkExpected: numericOperators,
    terraceWorkCollected: numericOperators,
    chargesExpected: numericOperators,
    chargesCollected: numericOperators,
    banakhatExpected: numericOperators,
    banakhatCollected: numericOperators,
    collectionCount: [
      { value: "=", label: "=" },
      { value: "!=", label: "!=" },
      { value: ">", label: ">" },
      { value: ">=", label: ">=" },
      { value: "<", label: "<" },
      { value: "<=", label: "<=" },
    ],
    daysSinceBooking: numericOperators,
    daysSinceLastCollection: numericOperators,
    unitType: textOperators,
    wing: textOperators,
    status: textOperators,
  };

  const textFields = ["unitType", "wing", "status"];

  // Load data on mount
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: ReceivableFilters | undefined =
        filterConditions.length > 0
          ? { logic: filterLogic, conditions: filterConditions }
          : undefined;

      const data = await receivableReportService.getReceivableReportData(
        selectedProject !== "all" ? selectedProject : undefined,
        dateFrom || undefined,
        dateTo || undefined,
        filters
      );

      setProjects(data.projects);
      setBookings(data.bookings);
    } catch (err: any) {
      console.error("Error loading receivable report:", err);
      setError(err.response?.data?.error || "Failed to load report data");
    } finally {
      setLoading(false);
    }
  };

  // Filter handlers
  const handleApplyFilters = () => loadData();

  const handleClearFilters = () => {
    setSelectedProject("all");
    setDateFrom("");
    setDateTo("");
    setFilterConditions([]);
    setFilterLogic("AND");
    setTimeout(() => loadData(), 50);
  };

  // Dynamic filter builder handlers
  const handleAddCondition = () => {
    setFilterConditions([
      ...filterConditions,
      { field: "pendingPercent", operator: ">", value: 0 },
    ]);
  };

  const handleRemoveCondition = (index: number) => {
    setFilterConditions(filterConditions.filter((_, i) => i !== index));
  };

  const handleConditionChange = (
    index: number,
    field: keyof FilterCondition,
    value: any
  ) => {
    const updated = [...filterConditions];
    updated[index] = { ...updated[index], [field]: value };

    if (field === "field") {
      const firstOperator = operatorsByField[value]?.[0]?.value || "=";
      updated[index].operator = firstOperator as any;
      updated[index].value = textFields.includes(value) ? "" : 0;
    }

    if (field === "operator") {
      if (value === "between") {
        updated[index].value = [0, 0];
      } else {
        updated[index].value = textFields.includes(updated[index].field) ? "" : 0;
      }
    }

    setFilterConditions(updated);
  };

  const handleBetweenValueChange = (
    index: number,
    position: 0 | 1,
    value: number
  ) => {
    const updated = [...filterConditions];
    const currentValue = updated[index].value as [number, number];
    const newValue: [number, number] = [...currentValue] as [number, number];
    newValue[position] = value;
    updated[index].value = newValue;
    setFilterConditions(updated);
  };

  // Export functionality
  const handleExport = () => {
    if (bookings.length === 0) {
      alert("No data to export");
      return;
    }

    const headers = [
      "Booking ID", "Booking Date", "Customer Name", "Contact", "Project",
      "Unit", "Type", "Wing",
      "Other Work Expected", "Other Work Collected",
      "Terrace Expected", "Terrace Collected",
      "Charges Expected", "Charges Collected",
      "Banakhat Expected", "Banakhat Collected",
      "Total Expected", "Total Collected", "Total Pending", "Pending %",
      "Collections", "Last Collection Date", "Last Collection Amt",
      "Days Since Booking", "Days Since Last Collection", "Status",
    ];

    const rows = bookings.map((b) => [
      b.bookingId, b.bookingDate, b.customerName, b.customerContact, b.projectName,
      b.unitRef, b.unitType, b.wing,
      b.otherWorkExpected, b.otherWorkCollected,
      b.terraceWorkExpected, b.terraceWorkCollected,
      b.chargesExpected, b.chargesCollected,
      b.banakhatExpected, b.banakhatCollected,
      b.totalExpected, b.totalCollected, b.totalPending, b.pendingPercent,
      b.collectionCount, b.lastCollectionDate || "N/A", b.lastCollectionAmount || "N/A",
      b.daysSinceBooking, b.daysSinceLastCollection ?? "N/A", b.status,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `receivable-report-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => window.print();

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <div className="follow-up-report-container">
      <div className="report-header">
        <h1>📊 Receivable Follow-Up Report</h1>
        <p className="report-subtitle">Track hidden income receivables - Other Work, Terrace, Charges, Banakhat</p>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyFilters(); } }}>
        <div className="filter-group">
          <label>Project:</label>
          <select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)}>
            <option value="all">All Projects</option>
            {projects.map((proj) => (
              <option key={proj.id} value={proj.id.toString()}>
                {proj.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Booking Date From:</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>

        <div className="filter-group">
          <label>Booking Date To:</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>

        <div className="filter-actions">
          <button onClick={handleApplyFilters} className="btn-apply">
            Apply Filters
          </button>
          <button onClick={handleClearFilters} className="btn-clear">
            Clear Filters
          </button>
          <button
            onClick={() => setShowFilterBuilder(!showFilterBuilder)}
            className="btn-advanced"
          >
            {showFilterBuilder ? "Hide" : "Show"} Advanced Filters
          </button>
        </div>
      </div>

      {/* Advanced Filter Builder */}
      {showFilterBuilder && (
        <div className="filter-builder">
          <div className="filter-builder-header">
            <h3>🔍 Advanced Filter Builder</h3>
            <div className="logic-toggle">
              <label>Logic:</label>
              <select
                value={filterLogic}
                onChange={(e) => setFilterLogic(e.target.value as "AND" | "OR")}
              >
                <option value="AND">AND (All conditions must match)</option>
                <option value="OR">OR (Any condition can match)</option>
              </select>
            </div>
          </div>

          <div className="conditions-list">
            {filterConditions.map((condition, index) => (
              <div key={index} className="condition-row">
                <span className="condition-number">{index + 1}.</span>

                <select
                  value={condition.field}
                  onChange={(e) => handleConditionChange(index, "field", e.target.value)}
                >
                  {filterFields.map((field) => (
                    <option key={field.value} value={field.value}>
                      {field.label}
                    </option>
                  ))}
                </select>

                <select
                  value={condition.operator}
                  onChange={(e) => handleConditionChange(index, "operator", e.target.value)}
                >
                  {operatorsByField[condition.field]?.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>

                {condition.operator === "between" ? (
                  <div className="between-inputs">
                    <input
                      type="number"
                      value={(condition.value as [number, number])[0]}
                      onChange={(e) =>
                        handleBetweenValueChange(index, 0, parseFloat(e.target.value))
                      }
                      placeholder="Min"
                    />
                    <span>to</span>
                    <input
                      type="number"
                      value={(condition.value as [number, number])[1]}
                      onChange={(e) =>
                        handleBetweenValueChange(index, 1, parseFloat(e.target.value))
                      }
                      placeholder="Max"
                    />
                  </div>
                ) : textFields.includes(condition.field) ? (
                  <input
                    type="text"
                    value={condition.value as string}
                    onChange={(e) => handleConditionChange(index, "value", e.target.value)}
                    placeholder="Enter value"
                  />
                ) : (
                  <input
                    type="number"
                    value={condition.value as number}
                    onChange={(e) =>
                      handleConditionChange(index, "value", parseFloat(e.target.value))
                    }
                    placeholder="Enter value"
                  />
                )}

                <button onClick={() => handleRemoveCondition(index)} className="btn-remove">
                  ✕
                </button>
              </div>
            ))}
          </div>

          <button onClick={handleAddCondition} className="btn-add-condition">
            + Add Condition
          </button>
        </div>
      )}

      {/* Export/Print Actions */}
      <div className="report-actions">
        <button onClick={handleExport} className="btn-export">
          📥 Export CSV
        </button>
        <button onClick={handlePrint} className="btn-print">
          🖨️ Print Report
        </button>
      </div>

      {/* Loading/Error */}
      {loading && <div className="loading">Loading report data...</div>}
      {error && <div className="error">{error}</div>}

      {!loading && !error && (
        <>
          {/* Bookings Table */}
          <div className="table-container">
            <table className="follow-up-table">
              <thead>
                <tr>
                  <th>Booking ID</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Contact</th>
                  <th>Project</th>
                  <th>Unit</th>
                  <th>Type</th>
                  <th>Wing</th>
                  <th>Other Work Exp</th>
                  <th>Other Work Col</th>
                  <th>Terrace Exp</th>
                  <th>Terrace Col</th>
                  <th>Charges Exp</th>
                  <th>Charges Col</th>
                  <th>Banakhat Exp</th>
                  <th>Banakhat Col</th>
                  <th>Adani Gas Exp</th>
                  <th>Adani Gas Col</th>
                  <th>Total Expected</th>
                  <th>Total Collected</th>
                  <th>Pending</th>
                  <th>Pending %</th>
                  <th>Collections</th>
                  <th>Last Collection</th>
                  <th>Last Amt</th>
                  <th>Days Since Booking</th>
                  <th>Days Since Last Col.</th>
                </tr>
              </thead>
              <tbody>
                {bookings.length === 0 ? (
                  <tr>
                    <td colSpan={27} className="no-data">
                      No bookings found with current filters
                    </td>
                  </tr>
                ) : (
                  bookings.map((booking) => (
                    <tr
                      key={booking.bookingId}
                      className={
                        booking.pendingPercent > 70 ||
                        (booking.daysSinceLastCollection !== null &&
                          booking.daysSinceLastCollection > 90)
                          ? "high-risk-row"
                          : ""
                      }
                    >
                      <td>{booking.bookingId}</td>
                      <td>{booking.bookingDate}</td>
                      <td>{booking.customerName}</td>
                      <td>{booking.customerContact}</td>
                      <td>{booking.projectName}</td>
                      <td>{booking.unitRef}</td>
                      <td>{booking.unitType}</td>
                      <td>{booking.wing}</td>
                      <td>{formatCurrency(booking.otherWorkExpected)}</td>
                      <td>{formatCurrency(booking.otherWorkCollected)}</td>
                      <td>{formatCurrency(booking.terraceWorkExpected)}</td>
                      <td>{formatCurrency(booking.terraceWorkCollected)}</td>
                      <td>{formatCurrency(booking.chargesExpected)}</td>
                      <td>{formatCurrency(booking.chargesCollected)}</td>
                      <td>{formatCurrency(booking.banakhatExpected)}</td>
                      <td>{formatCurrency(booking.banakhatCollected)}</td>
                      <td>{formatCurrency(booking.adaniGasExpected)}</td>
                      <td>{formatCurrency(booking.adaniGasCollected)}</td>
                      <td>{formatCurrency(booking.totalExpected)}</td>
                      <td>{formatCurrency(booking.totalCollected)}</td>
                      <td>{formatCurrency(booking.totalPending)}</td>
                      <td className="pending-percent">{booking.pendingPercent.toFixed(2)}%</td>
                      <td>{booking.collectionCount}</td>
                      <td>{booking.lastCollectionDate || "N/A"}</td>
                      <td>
                        {booking.lastCollectionAmount
                          ? formatCurrency(booking.lastCollectionAmount)
                          : "N/A"}
                      </td>
                      <td>{booking.daysSinceBooking}</td>
                      <td>{booking.daysSinceLastCollection ?? "N/A"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ReceivableReport;
